import React from "react";
import { Card } from "../../../components/shared/Card/Card";
import { useRequiredTenant } from "../../../contexts/TenantContext";
import { formatCurrency, formatPercent } from "../../../utils/formatters";

interface RentalAnalysisProps {
  propertyData: {
    estimatedValue: number;
    demographics?: {
      suggestedRent?: number;
      fmrEfficiency?: number;
      fmrOneBedroom?: number;
      fmrTwoBedroom?: number;
      fmrThreeBedroom?: number;
      fmrFourBedroom?: number;
      medianIncome?: number;
    };
  };
  className?: string;
}

export const RentalAnalysis: React.FC<RentalAnalysisProps> = ({
  propertyData,
  className = "",
}) => {
  const { colors } = useRequiredTenant().branding;

  // Calculate rental metrics
  const metrics = React.useMemo(() => {
    const monthlyRent = propertyData.demographics?.suggestedRent || 0;
    const annualRent = monthlyRent * 12;
    const grossYield = (annualRent / propertyData.estimatedValue) * 100;

    // Assume 15% expenses for net calculation
    const netYield = grossYield * 0.85;

    return {
      monthlyRent,
      annualRent,
      grossYield,
      netYield,
    };
  }, [propertyData]);

  const renderRentTier = (label: string, amount?: number) => {
    if (!amount) return null;

    return (
      <div
        className="p-4 rounded-lg bg-gray-50 border"
        style={{ borderColor: `${colors.primary}80` }}
      >
        <h3 className="text-sm font-medium" style={{ color: colors.text }}>
          {label}
        </h3>
        <p className="text-2xl font-bold" style={{ color: colors.primary }}>
          {formatCurrency(amount)}
        </p>
        <p className="text-sm" style={{ color: `${colors.text}99` }}>
          Fair Market Rent
        </p>
      </div>
    );
  };

  return (
    <Card title="Rental Market Analysis" className={className} elevation="md">
      {/* Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div
          className="p-4 rounded-lg bg-gray-50 border"
          style={{ borderColor: `${colors.primary}80` }}
        >
          <div className="text-sm" style={{ color: `${colors.text}99` }}>
            Suggested Rent
          </div>
          <div className="text-2xl font-bold" style={{ color: colors.primary }}>
            {formatCurrency(metrics.monthlyRent)}
          </div>
          <div className="text-sm" style={{ color: `${colors.text}99` }}>
            per month
          </div>
        </div>
        <div
          className="p-4 rounded-lg bg-gray-50 border"
          style={{ borderColor: `${colors.primary}80` }}
        >
          <div className="text-sm" style={{ color: `${colors.text}99` }}>
            Cap Rate
          </div>
          <div className="text-2xl font-bold" style={{ color: colors.primary }}>
            {formatPercent(metrics.netYield)}
          </div>
          <div className="text-sm" style={{ color: `${colors.text}99` }}>
            net yield
          </div>
        </div>
      </div>

      {/* Fair Market Rents */}
      {propertyData.demographics && (
        <div>
          <h3
            className="text-sm font-medium mb-4"
            style={{ color: colors.text }}
          >
            Fair Market Rents by Unit Type
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {renderRentTier("Studio", propertyData.demographics.fmrEfficiency)}
            {renderRentTier(
              "1 Bedroom",
              propertyData.demographics.fmrOneBedroom
            )}
            {renderRentTier(
              "2 Bedrooms",
              propertyData.demographics.fmrTwoBedroom
            )}
            {renderRentTier(
              "3 Bedrooms",
              propertyData.demographics.fmrThreeBedroom
            )}
            {renderRentTier(
              "4 Bedrooms",
              propertyData.demographics.fmrFourBedroom
            )}
          </div>
        </div>
      )}

      {/* Area Income */}
      {propertyData.demographics?.medianIncome && (
        <div className="mt-8">
          <div className="flex  items-center gap-10">
            <div className="flex flex-col">
              <div className="text-sm text-gray-500">Area Median Income</div>
              <div className="text-sm text-gray-400">
                Annual household income
              </div>
            </div>
            <div
              className="text-2xl font-bold"
              style={{ color: colors.primary }}
            >
              {formatCurrency(propertyData.demographics.medianIncome)}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default RentalAnalysis;
